import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Box,
  TextField,
  Button,
  Modal,
  Grid,
  Typography,
  MenuItem,
  IconButton,
  FormControl,
  Tooltip,
  Checkbox,
  ListItemText,
} from "@mui/material";
import ModalHeader from "../../modals/ModalHeader";
import { postJsonData, get } from "../../network/ApiController";
import ApiEndpoints from "../../network/ApiEndPoints";
import { okSuccessToast, apiErrorToast } from "../../utils/ToastUtil";
import ModalFooter from "../../modals/ModalFooter";
import EditIcon from "@mui/icons-material/Edit";
import AuthContext from "../../store/AuthContext";
import CloseIcon from "@mui/icons-material/Close";
import ViewModal from "../../modals/ViewModal";
import Spinner from "../../component/loading-screen/Spinner";
import SmallLoader from "../../component/loading-screen/SmallLoader";
import { fileToBase64, splitIntoFlatChunks } from "../../utils/ImageUtil";

const style = {
  maxWidth: 720,
  width: "95%",
  maxHeight: "90vh",
  overflowY: "auto",
  mx: "auto",
  my: 4,
  p: { xs: 2, sm: 3 },
  borderRadius: 2,
  backgroundColor: "background.paper",
};

const priorities = [
  { label: "High", value: "High" },
  { label: "Medium", value: "Medium" },
  { label: "Low", value: "Low" },
];

const taskTypes = [
  { value: "paid", label: "Paid" },
  { value: "maintenance", label: "Maintenance" },
];

const EditTask = ({ data, refresh, row, disabled }) => {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(false);
  const [updatedData, setUpdatedData] = useState({});
  const [developers, setDevelopers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [selectedWeightage, setSelectedWeightage] = useState("");
  const [weightage, setWeightage] = useState(null);
  const [developerName, setDeveloperName] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  console.log("EDIT TASK DATA:", updatedData);
  const authCtx = useContext(AuthContext);
  const user = authCtx.user;

  useEffect(() => {
    if (open && data) {
      // FIX: Handle assigned_to properly - it could be string, number, or array
      const assignedToValue = data?.assigned_to || "";
      let formattedAssignedTo = assignedToValue;

      // If it's a string containing comma-separated IDs, convert to array
      if (
        typeof assignedToValue === "string" &&
        assignedToValue.includes(",")
      ) {
        formattedAssignedTo = assignedToValue.split(",").map((id) => id.trim());
      }
      // If it's a single number or string, convert to array
      else if (assignedToValue && !Array.isArray(assignedToValue)) {
        formattedAssignedTo = [String(assignedToValue)];
      }

      setUpdatedData({
        id: data?.id || "",
        project_name: data?.project_name || "",
        project_id: data?.project_id ? String(data.project_id) : "",
        category: data?.category || "",
        start_date: data?.start_date || "",
        end_date: data?.end_date || "",
        assigned_to: formattedAssignedTo, // Use formatted value
        priority: data?.priority || "",
        remarks: data?.remarks || "",
        developer_remark: data?.developer_remark || "",
        description: data?.description || "",
        status: data?.status || "",
        task_img: data.task_img || "",
        task_type: data?.task_type || "",
        amount: data?.amount || "",
        merchant_response: data?.merchant_response || "",
      });
      setImagePreview(data.task_img || null);

      if (data?.weightage) {
        setWeightage(Number(data.weightage));
      }
    }
  }, [open, data]);

  const fetchDevelopers = useCallback(async () => {
    if (developers.length === 0) {
      setLoading(true);
      try {
        postJsonData(
          `${ApiEndpoints.GET_USERS_NAME}?role=developer`,
          "",
          setLoading,
          (res) => {
            setDevelopers(res.data || []);
          },
          (error) => apiErrorToast(error),
        );
      } catch (error) {
        apiErrorToast("Failed to fetch developers");
      } finally {
        setLoading(false);
      }
    }
  }, [developers]);

  const handleOpen = () => {
    fetchProjects();
    fetchDevelopers();
    fetchStatuses();
    fetchCategories();
    setOpen(true);
    setUploadedImage(null);
  };

  // Fetch projects list
  const fetchProjects = useCallback(async () => {
    if (projects.length === 0) {
      setLoading(true);
      try {
        get(
          ApiEndpoints.GET_PROJECTS,
          "",
          setLoading,
          (res) => {
            setProjects(res.data.data || []);
          },
          (error) => apiErrorToast(error),
        );
      } catch (error) {
        apiErrorToast("Failed to fetch projects");
      } finally {
        setLoading(false);
      }
    }
  }, [projects]);

  const fetchCategories = useCallback(() => {
    const url = `${ApiEndpoints.GET_UTILS}?type=category`;

    get(
      url,
      "",
      setLoading,
      (res) => {
        const categoryData = res.data.data || [];
        const formattedCategories = categoryData.map((item) => ({
          label: item.name,
          value: item.name,
        }));
        setCategories(formattedCategories);
      },
      (error) => apiErrorToast(error),
    );
  }, []);

  const fetchStatuses = useCallback(() => {
    const url = `${ApiEndpoints.GET_UTILS}?type=TaskStatus`;
    get(
      url,
      "",
      setLoading,
      (res) => {
        const statusData = res.data.data || [];
        const formattedStatuses = statusData.map((item) => ({
          label: item.name,
          value: item.name,
        }));
        setStatuses(formattedStatuses);
      },
      (error) => apiErrorToast(error),
    );
  }, []);

  // Handle changes in form fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (event) => {
    const { value } = event.target;
    console.log("Selected assigned_to values:", value); // Debug log

    // Ensure we always have an array
    const formattedValue = Array.isArray(value)
      ? value
      : value
        ? [String(value)]
        : [];

    setUpdatedData((prev) => ({
      ...prev,
      assigned_to: formattedValue,
    }));
  };
  const getDeveloperName = (developerId) => {
    if (!developerId || developers.length === 0) return "";
    const developer = developers.find(
      (dev) => String(dev.id) === String(developerId),
    );
    return developer ? developer.name : "";
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImagePreview(URL.createObjectURL(file));
      setUploadedImage(file);
      setUpdatedData({ ...updatedData, task_img: file });
    }
  };
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!updatedData.end_date) {
      apiErrorToast("End Date is required!");
      return;
    }

    const payload = {};

    /* -------------------------
       NORMAL FIELDS
    --------------------------*/
    Object.keys(updatedData).forEach((key) => {
      if (user.role === "Merchant" && key === "assigned_to") return;

      if (
        updatedData[key] !== null &&
        updatedData[key] !== undefined &&
        updatedData[key] !== "" &&
        key !== "task_img"
      ) {
        if (key === "assigned_to") {
          const val = updatedData[key];
          console.log("Processing assigned_to:", val); // Debug log

          // Handle assigned_to field - ensure it's sent as comma-separated string
          if (Array.isArray(val) && val.length > 0) {
            // Filter out empty strings and ensure all are strings
            const validValues = val.filter(
              (v) => v && v.toString().trim() !== "",
            );
            if (validValues.length > 0) {
              payload[key] = validValues.map((v) => String(v)).join(",");
            }
          } else if (val && typeof val === "string" && val.trim() !== "") {
            payload[key] = val;
          }
        } else {
          payload[key] = updatedData[key];
        }
      }
    });

    payload.project_id = updatedData.project_id;
    payload.project_name = updatedData.project_name;

    // Add developer_name to payload for better tracking
    if (payload.assigned_to && developers.length > 0) {
      const assignedIds = payload.assigned_to.split(",").map((id) => id.trim());
      const developerNames = assignedIds
        .map((id) => getDeveloperName(id))
        .filter((name) => name);

      if (developerNames.length > 0) {
        payload.assigned_name = developerNames.join(", ");
      }
    }

    if (user.role === "Admin" && weightage) {
      payload.weightage = weightage;
    }

    if (uploadedImage) {
      const base64 = await fileToBase64(uploadedImage);
      const imageChunks = splitIntoFlatChunks(base64, "task_img");
      Object.assign(payload, imageChunks);
    }

    if (!updatedData.id) {
      apiErrorToast("Task ID is missing!");
      return;
    }

    console.log("FINAL PAYLOAD", payload); // Debug log

    postJsonData(
      `${ApiEndpoints.EDIT_TASK1}/${updatedData.id}`,
      payload,
      setRequest,
      () => {
        okSuccessToast("Task updated successfully");
        setOpen(false);
        refresh();
      },
      (error) => apiErrorToast(error),
    );
  };

  const handleButtonClick = (buttonNumber) => {
    setWeightage(buttonNumber);
  };

  return (
    <Box>
      <Tooltip title="Edit Task">
        <IconButton
          sx={{
            color: "#0000ff",
          }}
          onClick={() => handleOpen()}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={style} className="sm_modal">
          <SmallLoader loading={request} />
          <ModalHeader title=" Edit Task" handleClose={() => setOpen(false)} />

          <Box
            component="form"
            id="EditTask"
            onSubmit={handleSubmit}
            noValidate
            autoComplete="off"
          >
            <Grid container spacing={1.2}>
              {/* Project ID */}
              <Grid item xs={6}>
                <Typography variant="body1" gutterBottom>
                  Project ID
                </Typography>
                <TextField
                  name="project_id"
                  type="number"
                  value={updatedData?.project_id || ""}
                  disabled
                  fullWidth
                  className="textfield"
                />
              </Grid>

              {/* Project Name (For Admin/Developer) */}
              {(user.role === "Admin" || user.role === "Developer") && (
                <Grid item xs={6}>
                  <Typography variant="body1" gutterBottom>
                    Project Name
                  </Typography>
                  <TextField
                    select
                    name="project_id"
                    value={updatedData?.project_id || ""}
                    onChange={(e) => {
                      const value = e.target.value;

                      const selectedProject = projects.find(
                        (p) => String(p.id) === String(value),
                      );

                      setUpdatedData((prev) => ({
                        ...prev,
                        project_id: value, // ✅ direct set
                        project_name: selectedProject?.name || "",
                      }));
                    }}
                    fullWidth
                  >
                    {projects.map((project) => (
                      <MenuItem key={project.id} value={String(project.id)}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {/* Start Date */}
              {user.role !== "Merchant" && (
                <Grid item xs={6}>
                  <Typography variant="body1" gutterBottom>
                    Start Date
                  </Typography>
                  <TextField
                    name="start_date"
                    type="date"
                    value={updatedData?.start_date || ""}
                    onChange={handleChange}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    className="textfield"
                  />
                </Grid>
              )}

              {/* End Date */}
              {user.role !== "Merchant" && (
                <Grid item xs={6}>
                  <Typography variant="body1" gutterBottom>
                    End Date
                  </Typography>
                  <TextField
                    name="end_date"
                    type="date"
                    value={updatedData?.end_date || ""}
                    onChange={handleChange}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    className="textfield"
                  />
                </Grid>
              )}

              {/* Category */}
              <Grid item xs={6}>
                <Typography variant="body1" gutterBottom>
                  Category
                </Typography>
                <TextField
                  select
                  name="category"
                  value={updatedData?.category || ""}
                  onChange={handleChange}
                  fullWidth
                  required
                  className="textfield"
                >
                  {categories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Assigned To (For Admin/Developer) */}
              {(user.role === "Admin" || user.role === "Developer") && (
                <Grid item xs={6}>
                  <Typography variant="body1" gutterBottom>
                    Assigned To
                  </Typography>
                  <TextField
                    select
                    name="assigned_to"
                    value={updatedData?.assigned_to || []}
                    onChange={handleMultiSelectChange}
                    onFocus={fetchDevelopers}
                    fullWidth
                    multiple
                    required
                    className="textfield"
                    SelectProps={{
                      renderValue: (selected) => {
                        if (!selected || selected.length === 0) {
                          return <em>Select developers</em>;
                        }
                        // Show developer names instead of IDs in the field
                        const selectedNames = selected.map((id) => {
                          const dev = developers.find(
                            (d) => String(d.id) === String(id),
                          );
                          return dev ? dev.name : id;
                        });
                        return selectedNames.join(", ");
                      },
                    }}
                  >
                    {developers?.map((developer) => (
                      <MenuItem key={developer.id} value={developer.id}>
                        <Checkbox
                          checked={updatedData?.assigned_to?.includes(
                            String(developer.id),
                          )}
                        />
                        <ListItemText primary={developer.name} />
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Show current assigned developer names for reference */}
                  {updatedData?.assigned_to &&
                    updatedData.assigned_to.length > 0 && (
                      <Typography variant="caption" color="textSecondary">
                        Currently assigned to:{" "}
                        {updatedData.assigned_to
                          .map((id) => getDeveloperName(id))
                          .join(", ")}
                      </Typography>
                    )}
                </Grid>
              )}

              {/* Priority */}
              <Grid item xs={6}>
                <Typography variant="body1" gutterBottom>
                  Priority
                </Typography>
                <TextField
                  select
                  name="priority"
                  value={updatedData?.priority || ""}
                  onChange={handleChange}
                  fullWidth
                  required
                  className="textfield"
                >
                  {priorities.map((priority) => (
                    <MenuItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Status */}
              <Grid item xs={6}>
                <Typography variant="body1" gutterBottom>
                  Status
                </Typography>
                <TextField
                  name="status"
                  select
                  value={updatedData?.status || ""}
                  onChange={handleChange}
                  fullWidth
                  required
                  className="textfield"
                >
                  {statuses.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Task Type (Admin only) */}
              {user.role === "Admin" && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="body1" gutterBottom>
                      Task Type
                    </Typography>
                    <TextField
                      name="task_type"
                      select
                      value={updatedData?.task_type || ""}
                      onChange={handleChange}
                      fullWidth
                      required
                      className="textfield"
                    >
                      {taskTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Amount Field (Only for paid tasks) */}
                  {updatedData.task_type === "paid" && (
                    <Grid item xs={6}>
                      <Typography variant="body1" gutterBottom>
                        Amount (₹)
                      </Typography>
                      <TextField
                        name="amount"
                        type="number"
                        value={updatedData?.amount || ""}
                        onChange={handleChange}
                        fullWidth
                        required
                        className="textfield"
                        inputProps={{ min: 0, step: "0.01" }}
                        placeholder="Enter amount in rupees"
                      />
                    </Grid>
                  )}
                </>
              )}

              {/* Description */}
              <Grid item xs={6}>
                <Typography variant="body1" gutterBottom>
                  Description
                </Typography>
                <TextField
                  name="description"
                  value={updatedData?.description || ""}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  className="textfield"
                />
              </Grid>

              {/* Developer Remark */}
              <Grid item xs={6}>
                <Typography variant="body1" gutterBottom>
                  Developer Remark
                </Typography>
                <TextField
                  name="developer_remark"
                  value={updatedData?.developer_remark || ""}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  className="textfield"
                />
              </Grid>

              {/* Weightage Selection (Admin only) */}
              {user.role === "Admin" && (
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    sx={{ mt: 2 }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      sx={{ width: "100%" }}
                    >
                      {[...Array(10).keys()].map((number) => {
                        const buttonNumber = number + 1;
                        let buttonColor;

                        if (buttonNumber === 1 || buttonNumber === 2) {
                          buttonColor = "red";
                        } else if (buttonNumber === 3) {
                          buttonColor = "orange";
                        } else if ([4, 5, 6].includes(buttonNumber)) {
                          buttonColor = "#FFC000";
                        } else if ([7, 8].includes(buttonNumber)) {
                          buttonColor = "#AAFF00";
                        } else {
                          buttonColor = "#006400";
                        }

                        const isSelected = weightage === buttonNumber;

                        return (
                          <Button
                            key={buttonNumber}
                            variant={isSelected ? "contained" : "outlined"}
                            color="primary"
                            onClick={() => handleButtonClick(buttonNumber)}
                            sx={{
                              backgroundColor: isSelected
                                ? "#000"
                                : buttonColor,
                              borderColor: isSelected ? "#000" : buttonColor,
                              color: "white",
                              "&:hover": {
                                backgroundColor: isSelected
                                  ? "#333"
                                  : buttonColor,
                              },
                            }}
                          >
                            {buttonNumber}
                          </Button>
                        );
                      })}
                    </Box>

                    {weightage && (
                      <Box
                        sx={{
                          mt: 2,
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: "#555",
                        }}
                      >
                        <p>Selected Weightage: {weightage * 10}%</p>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Image Upload */}
              <Grid item xs={12}>
                <Typography>Upload New Task Image</Typography>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ width: "100%", marginBottom: "10px" }}
                />

                {!uploadedImage && row?.task_img && (
                  <Box sx={{ mb: 2 }}>
                    <ViewModal
                      row={{ fileName: row.task_img }}
                      title="View Task Image"
                    />
                  </Box>
                )}

                {uploadedImage && (
                  <Box sx={{ mt: 1 }}>
                    <img
                      src={imagePreview}
                      alt="New Preview"
                      width="100"
                      height="100"
                    />
                  </Box>
                )}
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <ModalFooter form="EditTask" request={request} btn="Update" />
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default EditTask;
